export const featureKey = (step) => `${step.featureID}:${step.learningRevision}`;

export const guideTransitionTimer = featureID => {
    if (typeof __DEV__ === 'undefined' || !__DEV__) return null;
    const started = Date.now();
    return phase => console.debug('[guides:transition]', { featureID, phase, elapsedMs: Date.now() - started });
};

export const mergeProgress = (existing = [], incoming = []) => {
    const rows = new Map(existing.map(row => [featureKey(row), row]));
    incoming.forEach(row => {
        const previous = rows.get(featureKey(row));
        rows.set(featureKey(row), previous?.status === 'viewed' && row.status !== 'viewed' ? previous : { ...previous, ...row });
    });
    return [...rows.values()];
};

export const eligibleSteps = (guide, context) => {
    if (!context?.user?.id || !context.organizationID || !guide?.eligible(context)) return [];
    return guide.steps.filter(step => step.eligible(context) === true).map(step => {
        const dynamic = ['dependsOn', 'opensSurface', 'waitFor'].filter(key => typeof step[key] === 'function');
        return dynamic.length ? { ...step, ...Object.fromEntries(dynamic.map(key => [key, step[key](context)])) } : step;
    });
};

export const guideSurfaceIDs = (guide, surfaceID) => {
    const ids = [];
    while (surfaceID && !ids.includes(surfaceID)) {
        ids.push(surfaceID);
        surfaceID = guide?.surfaces?.[surfaceID]?.parent;
    }
    return ids;
};

export const pendingSteps = (steps, progress = []) => {
    const handled = new Set(progress.map(featureKey));
    return steps.filter(step => !handled.has(featureKey(step)));
};

export const resumeSteps = (steps, progress = []) => {
    const byID = new Map(steps.map(step => [step.featureID, step]));
    const included = new Set();
    const visiting = new Set();
    const result = [];
    const append = step => {
        if (!step || visiting.has(step.featureID)) return false;
        if (included.has(step.featureID)) return true;
        visiting.add(step.featureID);
        const start = result.length;
        const dependencies = step.dependsOn || (step.surface ? [steps.find(item => !item.surface && item.targetID === step.surface)?.featureID] : []);
        const available = dependencies.every(id => append(byID.get(id)));
        visiting.delete(step.featureID);
        if (!available) {
            result.splice(start).forEach(item => included.delete(item.featureID));
            return false;
        }
        included.add(step.featureID);
        result.push(step);
        return true;
    };
    pendingSteps(steps, progress).forEach(append);
    return result;
};

export const guideStatus = (steps, progress = []) => {
    if (!steps.length) return { label: 'Unavailable', viewed: 0, total: 0, canContinue: false };
    const rows = new Map(progress.map(row => [featureKey(row), row]));
    const viewed = steps.filter(step => rows.get(featureKey(step))?.status === 'viewed').length;
    const handled = steps.filter(step => rows.has(featureKey(step))).length;
    const updated = steps.some(step => !rows.has(featureKey(step)) && progress.some(row => row.featureID === step.featureID && row.learningRevision < step.learningRevision));
    const label = handled === steps.length ? (viewed === steps.length ? 'Viewed' : 'Skipped') : updated ? 'Updated' : handled ? 'In progress' : 'New';
    return { label, viewed, total: steps.length, canContinue: handled > 0 && handled < steps.length };
};

export const validateGuides = (guides) => {
    const guideIDs = new Set();
    const features = new Set();
    for (const guide of guides) {
        if (!guide.id || guideIDs.has(guide.id) || typeof guide.eligible !== 'function') throw new Error('Invalid guide definition');
        guideIDs.add(guide.id);
        const previous = new Map();
        for (const step of guide.steps) {
            if (!/^[a-z][a-z0-9_.-]{0,119}$/.test(step.featureID) || features.has(step.featureID) || !Number.isInteger(step.learningRevision) || step.learningRevision < 1 || !step.targetID || !step.title || !step.body || typeof step.eligible !== 'function') throw new Error('Invalid guide step');
            if (step.dependsOn !== undefined && typeof step.dependsOn !== 'function' && (!Array.isArray(step.dependsOn) || step.dependsOn.some(id => previous.get(id)?.interaction !== 'activate'))) throw new Error('Invalid guide dependency');
            features.add(step.featureID);
            previous.set(step.featureID, step);
        }
        for (const [id, surface] of Object.entries(guide.surfaces || {})) {
            if (previous.get(surface.opener)?.interaction !== 'activate' || (surface.parent && !guide.surfaces[surface.parent]) || (surface.cancel && surface.cancel !== 'retry')) throw new Error('Invalid guide surface');
            const seen = new Set();
            let current = id;
            while (current) {
                if (seen.has(current)) throw new Error('Cyclic guide surface');
                seen.add(current);
                current = guide.surfaces[current]?.parent;
            }
        }
    }
    return guides;
};

export const spotlightPath = (target, width, height) => {
    'worklet';
    if (!target) return '';
    const x = Math.max(0, target.x - 6);
    const y = Math.max(0, target.y - 6);
    const right = Math.min(width, target.x + target.width + 6);
    const bottom = Math.min(height, target.y + target.height + 6);
    const radius = Math.min(12, (right - x) / 2, (bottom - y) / 2);
    if (radius <= 0) return '';
    return `M${x + radius} ${y} H${right - radius} Q${right} ${y} ${right} ${y + radius} V${bottom - radius} Q${right} ${bottom} ${right - radius} ${bottom} H${x + radius} Q${x} ${bottom} ${x} ${bottom - radius} V${y + radius} Q${x} ${y} ${x + radius} ${y} Z`;
};

// Curved connector between the guide card and the spotlighted target.
// Returns the quadratic path plus the endpoint dot position. Runs on the UI
// thread via Reanimated, so it must stay dependency-free.
export const guideArrowPath = (cardX, cardY, cardW, cardH, target) => {
    'worklet';
    if (!target || cardW <= 0 || cardH <= 0) return { path: '', dot: null };
    const edgePoint = (cx, cy, w, h, tx, ty) => {
        'worklet';
        const dx = tx - cx;
        const dy = ty - cy;
        if (!dx && !dy) return { x: cx, y: cy };
        const sx = dx !== 0 ? (w / 2) / Math.abs(dx) : 100000;
        const sy = dy !== 0 ? (h / 2) / Math.abs(dy) : 100000;
        const scale = Math.min(sx, sy);
        return { x: cx + dx * scale, y: cy + dy * scale };
    };
    const cx = cardX + cardW / 2;
    const cy = cardY + cardH / 2;
    const tx = target.x + target.width / 2;
    const ty = target.y + target.height / 2;
    const start = edgePoint(cx, cy, cardW, cardH, tx, ty);
    const end = edgePoint(tx, ty, target.width, target.height, cx, cy);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.sqrt(dx * dx + dy * dy) || 1;
    const bend = Math.min(48, length * 0.35);
    const controlX = (start.x + end.x) / 2 - (dy / length) * bend;
    const controlY = (start.y + end.y) / 2 + (dx / length) * bend;
    return { path: `M${start.x} ${start.y} Q${controlX} ${controlY} ${end.x} ${end.y}`, dot: end };
};

export const interpolateGuideLayout = (from, to, progress) => {
    const amount = Math.max(0, Math.min(1, progress));
    const mix = (a, b) => a + (b - a) * amount;
    const target = from.target && to.target
        ? Object.fromEntries(['x', 'y', 'width', 'height'].map(key => [key, mix(from.target[key], to.target[key])]))
        : amount === 1 ? to.target : from.target || to.target;
    return {
        target,
        placement: Object.fromEntries(['left', 'top', 'width'].map(key => [key, mix(from.placement[key], to.placement[key])])),
        cardHeight: mix(from.cardHeight, to.cardHeight),
    };
};

export const placeGuideCard = ({ target, width, height, cardHeight, insetTop = 0, insetBottom = 0 }) => {
    const margin = 16;
    const cardWidth = Math.min(360, width - margin * 2);
    const maxTop = Math.max(insetTop + margin, height - insetBottom - cardHeight - margin);
    if (width < 700 || !target) return { left: (width - cardWidth) / 2, top: Math.min(maxTop, target && target.y > height / 2 ? insetTop + margin : maxTop), width: cardWidth };
    let left = target.x + target.width + margin;
    if (left + cardWidth > width - margin) left = target.x - cardWidth - margin;
    if (left < margin) left = Math.max(margin, Math.min(target.x, width - cardWidth - margin));
    let top = Math.max(insetTop + margin, Math.min(target.y, maxTop));
    const overlaps = left < target.x + target.width && left + cardWidth > target.x && top < target.y + target.height && top + cardHeight > target.y;
    if (overlaps) top = target.y + target.height + margin <= maxTop ? target.y + target.height + margin : Math.max(insetTop + margin, target.y - cardHeight - margin);
    return { left, top, width: cardWidth };
};
